import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePushNotifications } from '../hooks/usePushNotifications';
import useNotificationFeedStore from '../store/useNotificationFeedStore';

const BROADCAST_CHANNEL_NAME = 'postreams-push';

const invalidateForPayload = (queryClient, payload) => {
  const data = payload?.data || {};
  const type = data.type;

  if (type === 'post') {
    if (data.post_id) {
      queryClient.invalidateQueries({ queryKey: ['post-details', data.post_id] });
      queryClient.invalidateQueries({ queryKey: ['post-details-poll', data.post_id] });
    }
    queryClient.invalidateQueries({ queryKey: ['posts'] });
  } else if (type === 'campaign') {
    if (data.campaign_id) {
      queryClient.invalidateQueries({ queryKey: ['campaign-posts', data.campaign_id] });
    }
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }
};

const PushNotificationFeedBridge = () => {
  const queryClient = useQueryClient();
  const addNotification = useNotificationFeedStore((s) => s.addNotification);
  const { supported, configured, subscribeForeground } = usePushNotifications();

  useEffect(() => {
    if (!supported || !configured) return undefined;

    let unsubscribe = null;
    let cancelled = false;

    const handlePayload = (payload) => {
      if (!payload) return;
      addNotification(payload);
      invalidateForPayload(queryClient, payload);
    };

    (async () => {
      const unsub = await subscribeForeground((payload) => {
        handlePayload(payload);
      });
      if (cancelled) {
        if (typeof unsub === 'function') unsub();
        return;
      }
      unsubscribe = unsub;
    })();

    let channel = null;
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.addEventListener('message', (event) => {
        handlePayload(event.data);
      });
    }

    return () => {
      cancelled = true;
      if (typeof unsubscribe === 'function') unsubscribe();
      if (channel) channel.close();
    };
  }, [supported, configured, subscribeForeground, addNotification, queryClient]);

  return null;
};

export default PushNotificationFeedBridge;
